import { useQuery, useQueryClient } from "@tanstack/react-query";

import { listContentFlags, listThoughtFlags } from "@/lib/admin-api";

import { useRealtimeChannel } from "./use-realtime-channel";

const MODERATION_PENDING_COUNT_QUERY_KEY = ["moderation", "pending-count"];

/**
 * Pending moderation-queue count for the sidebar badge. Cheap by
 * construction: both list endpoints already return a `pageInfo.totalCount`
 * that moderation-queue.tsx relies on for its own "Total Flags" readout and
 * pagination, so asking for it here costs a `page_size: 1` request instead of
 * pulling the queue itself.
 *
 * Returns `undefined` (render nothing) rather than a fabricated number if the
 * backend ever omits `totalCount` for both flag types - a `page_size: 1`
 * response's `items.length` is not a usable stand-in for a total.
 *
 * No polling: the count is fetched once (subject to the shared react-query
 * `staleTime`) and invalidated on the same realtime channel the moderation
 * queue page already subscribes to, so it updates live off the same event
 * instead of an interval.
 */
export function useModerationPendingCount(): number | undefined {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: MODERATION_PENDING_COUNT_QUERY_KEY,
    queryFn: async () => {
      const [content, thought] = await Promise.all([
        listContentFlags({ status: "FLAG_STATUS_PENDING", pageSize: 1, page: 1 }),
        listThoughtFlags({ status: "FLAG_STATUS_PENDING", pageSize: 1, page: 1 }),
      ]);

      const contentTotal = content.pageInfo?.totalCount;
      const thoughtTotal = thought.pageInfo?.totalCount;
      if (contentTotal == null && thoughtTotal == null) {
        // react-query treats `undefined` from a queryFn as a usage error, so
        // `null` is the sentinel that comes back out of the cache - translated
        // to `undefined` below, which is the "render nothing" contract this
        // hook actually promises its caller.
        return null;
      }
      return (contentTotal ?? 0) + (thoughtTotal ?? 0);
    },
  });

  useRealtimeChannel("admin:moderation:queue", () => {
    queryClient.invalidateQueries({ queryKey: MODERATION_PENDING_COUNT_QUERY_KEY });
  });

  return data ?? undefined;
}
